import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Gift, Pencil, Plus, Receipt, Search, Trash2 } from "lucide-react";
import { api, apiError, downloadCsv, formatDate, money, moneyExact, todayISO } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import StickyFilters from "@/components/StickyFilters";
import DateRange, { ALL_TIME, rangeNote, withinRange } from "@/components/DateRange";
import { useSettings } from "@/context/SettingsContext";
import Layout from "@/components/Layout";
import { useTenant } from "@/context/TenantContext";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import ItemPicker from "@/components/ItemPicker";
import { EmptyState, ItemCode, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export default function Sales() {
  const confirm = useConfirm();
  const { settings, labelFor } = useSettings();
  const sym = settings.currency_symbol;
  const { tenantId } = useTenant();
  const CK = cacheKey("sales", tenantId);
  const cached = cacheGet(CK);
  const [rows, setRows] = useState(() => cached?.rows || []);
  const [items, setItems] = useState(() => cached?.items || []);
  const [loading, setLoading] = useState(() => !cached);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState(ALL_TIME);
  const [kind, setKind] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    item_code: "",
    quantity: "1",
    selling_price: "",
    customer: "",
    channel: "",
    sale_date: todayISO(),
  });

  // Ignore a reply that lands after the client has been switched — otherwise
  // one business's figures can appear under another's name.
  const currentKey = useRef(CK);
  useEffect(() => {
    currentKey.current = CK;
  }, [CK]);

  const load = useCallback(async () => {
    const askedFor = CK;
    if (!cacheGet(CK)) setLoading(true);
    try {
      const [s, i] = await Promise.all([api.get("/sales"), api.get("/items")]);
      if (currentKey.current !== askedFor) return;
      setRows(s.data);
      setItems(i.data);
      cacheSet(CK, { rows: s.data, items: i.data });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [CK]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const inRange = (row) => withinRange(row.sale_date, range);
    const q = query.trim().toLowerCase();
    let base = rows.filter(inRange);
    if (kind === "gift_box") base = base.filter((r) => r.kind === "gift_box");
    if (kind === "item") base = base.filter((r) => r.kind !== "gift_box");
    if (!q) return base;
    return base.filter(
      (r) =>
        r.item_code.toLowerCase().includes(q) ||
        (r.item_name || "").toLowerCase().includes(q) ||
        (r.customer || "").toLowerCase().includes(q)
    );
  }, [rows, query, kind, range]);

  const totals = useMemo(
    () => ({
      revenue: visible.reduce((t, r) => t + (r.revenue || 0), 0),
      profit: visible.reduce((t, r) => t + (r.profit || 0), 0),
      units: visible.reduce((t, r) => t + (r.quantity || 0), 0),
    }),
    [visible]
  );

  const selected = items.find((i) => i.item_code === form.item_code);

  const openEditor = (row) => {
    setEditingId(row.sale_id);
    setForm({
      item_code: row.item_code,
      quantity: String(row.quantity),
      selling_price: String(row.selling_price),
      customer: row.customer || "",
      channel: row.channel || "",
      sale_date: row.sale_date,
    });
    setAdding(true);
  };

  const openDialog = () => {
    setEditingId(null);
    setForm({
      item_code: "",
      quantity: "1",
      selling_price: "",
      customer: "",
      channel: "",
      sale_date: todayISO(),
    });
    setAdding(true);
  };

  const submit = async () => {
    if (!form.item_code || !Number(form.quantity)) {
      toast.error("Choose an item and enter a quantity");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        selling_price: form.selling_price === "" ? undefined : Number(form.selling_price),
      };
      if (editingId) {
        await api.put(`/sales/${editingId}`, payload);
        toast.success("Sale updated — stock adjusted");
      } else {
        await api.post("/sales", payload);
        toast.success("Sale recorded — stock updated");
      }
      setAdding(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: `Remove this sale of ${row.item_name}?`,
      body: `${row.quantity} unit${row.quantity === 1 ? "" : "s"} will go back into stock, and the revenue comes off your totals.`,
      confirmText: "Remove sale",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/sales/${row.sale_id}`);
      toast.success("Sale removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Sales"
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading
          ? undefined
          : `${visible.length} sales · ${totals.units} units · ${money(
              totals.revenue,
              sym
            )} revenue${rangeNote(range)}`
      }
      action={
        <>
          <Button
            variant="outline"
            onClick={() => downloadCsv("/export/sales.csv", "sales-log.csv")}
            className="hidden sm:inline-flex"
          >
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Record sale</span>
          </Button>
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        {[
          { label: "Revenue", value: money(totals.revenue, sym) },
          { label: "Profit", value: money(totals.profit, sym) },
          { label: "Units sold", value: totals.units },
        ].map((s) => (
          <div key={s.label} className="panel px-5 py-4">
            <div className="eyebrow">{s.label}</div>
            <div className="font-display text-[22px] font-semibold text-ink mt-1.5 tnum">
              {s.value}
            </div>
          </div>
        ))}
      </div>

      <StickyFilters>
        <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by item or customer"
            className="pl-9 border-line"
          />
        </div>
        {/* A box sale is one line for a bundle, so mixing them with item sales
            makes both harder to read. */}
        {rows.some((r) => r.kind === "gift_box") && (
          <div className="flex gap-0.5 bg-white border border-line rounded-lg p-0.5 shrink-0">
            {[
              { key: "all", label: "All" },
              { key: "item", label: "Items" },
              { key: "gift_box", label: labelFor("gift_boxes") },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setKind(f.key)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-colors whitespace-nowrap",
                  kind === f.key ? "bg-brand-light text-brand" : "text-muted2 hover:text-ink"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
          <DateRange value={range} onChange={setRange} />
        </div>
      </StickyFilters>

      {loading ? (
        <LoadingPanel rows={7} />
      ) : visible.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={Receipt}
            title={query ? "No sales match that search" : "No sales recorded"}
            body={
              query
                ? "Try a different item or customer name."
                : "Recording a sale takes the units out of stock and books the revenue."
            }
            action={
              !query && (
                <Button onClick={openDialog} className="bg-brand hover:bg-brand-hover">
                  <Plus className="w-4 h-4 mr-2" /> Record sale
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-line bg-surface/60">
                  {["Date", "Code", "Item", "Customer", "Qty", "Price", "Revenue", "Profit", ""].map(
                    (h, idx) => (
                      <th
                        key={h + idx}
                        className={`t-head px-4 py-3 text-left ${
                          ["Qty", "Price", "Revenue", "Profit"].includes(h) ? "text-right" : ""
                        }`}
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.sale_id} className="t-row last:border-0 group">
                    <td className="t-cell text-muted2 whitespace-nowrap">
                      {formatDate(r.sale_date)}
                    </td>
                    <td className="t-cell">
                      {r.kind === "gift_box" ? (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-brand">
                          <Gift className="w-3.5 h-3.5" />
                          Box
                        </span>
                      ) : (
                        <ItemCode code={r.item_code} />
                      )}
                    </td>
                    <td className="t-cell font-medium max-w-[220px] truncate">{r.item_name}</td>
                    <td className="t-cell text-muted2 max-w-[160px] truncate">
                      {r.customer || "—"}
                    </td>
                    <td className="t-cell num font-mono">{r.quantity}</td>
                    <td className="t-cell num text-muted2">{money(r.selling_price, sym)}</td>
                    <td className="t-cell num font-medium">{moneyExact(r.revenue, sym)}</td>
                    <td
                      className={cn(
                        "t-cell num",
                        (r.profit || 0) >= 0 ? "text-stock-in" : "text-stock-out"
                      )}
                    >
                      {moneyExact(r.profit, sym)}
                    </td>
                    <td className="t-cell text-right whitespace-nowrap">
                      {r.kind !== "gift_box" && (
                      <button
                        onClick={() => openEditor(r)}
                        className="text-muted2 hover:text-brand sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 mr-1"
                        title="Edit sale"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      )}
                      <button
                        onClick={() => remove(r)}
                        className="text-muted2 hover:text-stock-out sm:opacity-40 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1"
                        title="Remove sale"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingId ? "Edit this sale" : "Record a sale"}
            </DialogTitle>
            <DialogDescription>
              Leave the price blank to use the item's usual selling price.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Item</Label>
              <ItemPicker
                items={items}
                value={form.item_code}
                onChange={(code) => setForm({ ...form, item_code: code, selling_price: "" })}
              />
              {selected && (
                <p className="text-[12px] text-muted2">
                  {selected.current_stock} on hand · usually sells for{" "}
                  {money(selected.selling_price, sym)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sq">Quantity</Label>
                <Input
                  id="sq"
                  type="number"
                  min="1"
                  max={selected?.current_stock || undefined}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp">Price each</Label>
                <Input
                  id="sp"
                  type="number"
                  min="0"
                  value={form.selling_price}
                  onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
                  placeholder={selected ? String(selected.selling_price) : "Usual price"}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sc">Customer</Label>
                <Input
                  id="sc"
                  value={form.customer}
                  onChange={(e) => setForm({ ...form, customer: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sch">Channel</Label>
                <Input
                  id="sch"
                  value={form.channel}
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  placeholder="Instagram, market…"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sd">Sale date</Label>
              <Input
                id="sd"
                type="date"
                value={form.sale_date}
                onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
              />
            </div>

            {selected && form.quantity && (
              <div className="rounded-lg bg-brand-light px-3.5 py-2.5 text-[13px] text-brand flex items-center justify-between">
                <span>Revenue</span>
                <span className="font-mono font-semibold">
                  {moneyExact(
                    Number(form.quantity) *
                      (form.selling_price === ""
                        ? selected.selling_price
                        : Number(form.selling_price)),
                    sym
                  )}
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover">
              {editingId ? "Save changes" : "Record sale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
